import { ConversationParticipantEntity } from '@provision/conversation-types';
import { ConversationParticipant } from '@provision/entity-types';
import { useMutation } from '@provision/graphql-client';
import { useCallback } from 'react';
import { Address } from 'src/graphql/entities/address';
import { SAVE_CLIENT_WITH_RELATED_DATA_MUTATION } from 'src/graphql/entities/client/client.operations';
import { Client } from 'src/graphql/entities/client/client.types';
import { parseSaveClientErrorMessage } from 'src/graphql/entities/conversation-participants/parse-save-client-error-message';
import { NullableDeepPartial } from 'src/graphql/types';
import { generateFieldModifiersMap } from 'src/graphql/utils';
import { ClientRaw } from 'src/types';
import { useNotifications } from 'src/utils/hooks';

export type SaveClientWithRelatedDataValues = Partial<
  Pick<
    Client,
    | 'firstName'
    | 'lastName'
    | 'notes'
    | 'marketingOptOut'
    | 'partnerId'
    | 'primaryClientEmailId'
    | 'primaryEmail'
    | 'primaryEmailLabelId'
    | 'primaryClientPhoneNumberId'
    | 'primaryPhoneNumber'
    | 'primaryPhoneNumberLabelId'
    | 'emails'
    | 'phoneNumbers'
  >
> & {
  clientId?: string;
  address?: Partial<Address> | null | void;
  participant?: Partial<ConversationParticipant> | null | void;
};

export const useSaveClientWithRelatedData = () => {
  const { showErrorAlert } = useNotifications();

  const [saveClientWithRelatedDataMutation, { loading }] = useMutation<{
    saveClientWithRelatedData: {
      client: NullableDeepPartial<ClientRaw> & {
        __typename: string;
      };
    };
  }>(SAVE_CLIENT_WITH_RELATED_DATA_MUTATION, {
    onError: (error, clientOptions) => {
      console.error(error);
      const message = parseSaveClientErrorMessage(error.message);
      showErrorAlert(
        `Unable to ${clientOptions?.variables?.clientId ? 'update client' : 'create client'}. ${message}`,
      );
    },
  });

  const saveClientWithRelatedData = useCallback(
    async (values: SaveClientWithRelatedDataValues) => {
      const {
        clientId,
        firstName,
        lastName,
        notes,
        marketingOptOut,
        partnerId,
        primaryClientEmailId,
        primaryEmail,
        primaryEmailLabelId,
        primaryClientPhoneNumberId,
        primaryPhoneNumber,
        primaryPhoneNumberLabelId,
        emails,
        phoneNumbers,
        address,
        participant,
      } = values;

      const createdAt = new Date().toISOString();
      const tempId = `temp-id${createdAt}`;

      const { data } = await saveClientWithRelatedDataMutation({
        variables: {
          clientId,
          partnerId,
          firstName,
          lastName,
          primaryClientEmailId,
          primaryEmail,
          primaryClientPhoneNumberId,
          primaryPhoneNumber: primaryPhoneNumber ? parseInt(primaryPhoneNumber) : undefined,
          emails: emails?.map((email) => ({
            clientEmailId: email.id,
            value: email.email,
            labelId: email.labelId || undefined,
          })),
          phoneNumbers: phoneNumbers?.map((phoneNumber) => ({
            clientPhoneNumberId: phoneNumber.id,
            value: phoneNumber.number,
            labelId: phoneNumber.labelId || undefined,
          })),
          notes,
          marketingOptOut,
          address: address
            ? {
                addressId: address.addressId,
              }
            : undefined,
          participant: participant
            ? {
                conversationParticipantId:
                  participant.id ?? participant.conversationParticipantId,
                partnerId: participant.partnerId,
                participantEntity: participant.participantEntity,
                participantIdentity: participant.participantIdentity,
              }
            : undefined,
        },
        optimisticResponse: {
          saveClientWithRelatedData: {
            client: {
              __typename: 'Client',
              id: clientId ?? tempId,
              clientId: clientId ?? tempId,
              firstName,
              lastName,
              partnerId,
              notes: notes ?? '',
              marketingOptOut: marketingOptOut ?? false,
              primaryEmail: primaryEmail
                ? {
                    id: primaryClientEmailId ?? tempId,
                    value: primaryEmail,
                    labelId: primaryEmailLabelId ?? 0,
                  }
                : null,
              primaryPhoneNumber: primaryPhoneNumber
                ? {
                    id: primaryClientPhoneNumberId ?? tempId,
                    value: parseInt(primaryPhoneNumber),
                    labelId: primaryPhoneNumberLabelId ?? 0,
                  }
                : null,
              phoneNumbers: phoneNumbers?.map((phoneNumber) => ({
                id: phoneNumber.id ?? tempId,
                clientPhoneNumberId: phoneNumber.id,
                value: phoneNumber.number,
                labelId: phoneNumber.labelId ?? 0,
              })),
              emails:
                emails?.map((email) => ({
                  id: email.id ?? tempId,
                  clientEmailId: email.id,
                  value: email.email,
                  labelId: email.labelId ?? 0,
                })) ?? [],
              createdAt,
              updatedAt: createdAt,
            },
          },
        },
        update(cache, { data }) {
          const updatedClient = data?.saveClientWithRelatedData?.client;
          if (!updatedClient?.clientId) return;

          if (clientId) {
            const id = cache.identify({
              __typename: 'Client',
              id: clientId,
            });

            const fields = generateFieldModifiersMap({
              firstName,
              lastName,
              notes,
              marketingOptOut,
            });

            cache.modify({
              id,
              fields: {
                ...fields,
                emails(_, { toReference }) {
                  return updatedClient.emails
                    ?.filter((email) => !!email)
                    .map((email) =>
                      toReference(
                        {
                          ...email,
                          __typename: 'ClientEmail',
                        },
                        true,
                      ),
                    );
                },
                primaryEmail() {
                  return updatedClient.primaryEmail;
                },
                phoneNumbers(_, { toReference }) {
                  return updatedClient.phoneNumbers
                    ?.filter((phone) => !!phone)
                    .map((phone) =>
                      toReference(
                        {
                          ...phone,
                          __typename: 'ClientPhoneNumber',
                        },
                        true,
                      ),
                    );
                },
                primaryPhoneNumber() {
                  return updatedClient.primaryPhoneNumber;
                },
              },
            });
          } else {
            // New client: evict every cached getClient list field so the Clients grid
            // and any client search refetch and pick it up. Normalized Client objects are
            // left untouched, so mounted single-client views (useFragment) keep their data
            // — hence no cache.gc() here.
            cache.evict({ id: 'ROOT_QUERY', fieldName: 'getClient' });
          }

          if (participant) {
            const id = cache.identify({
              __typename: 'ConversationParticipant',
              id: participant.id,
            });

            const fields = generateFieldModifiersMap({
              participantEntity: ConversationParticipantEntity.Client,
            });

            cache.modify({
              id,
              fields: {
                ...fields,
                client(_, { toReference }) {
                  return toReference(updatedClient, true);
                },
              },
            });
          }
        },
      });

      return data?.saveClientWithRelatedData?.client;
    },
    [saveClientWithRelatedDataMutation],
  );

  return { saveClientWithRelatedData, isLoading: loading };
};
